import { MoreHorizontal } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Question } from '@/api/questionSets';
import { Button } from '@/components/ui/Button';
import { DropdownMenu, type MenuItem } from '@/components/ui/DropdownMenu';

import { QuestionDialog } from './QuestionDialog';
import { RemoveQuestionDialog } from './RemoveQuestionDialog';

interface Props {
  /** The set that owns this question — the address of every write, and the write gate itself. */
  setId: number;
  question: Question;
  /**
   * Called once *Remove* or *Restore* has been accepted, with the row it was about and whether it is
   * now active. The table announces it and — when the row is leaving the table — moves focus, because
   * the `⋯` below is the control focus would otherwise return to and it unmounts with its row (a
   * `ui-reviewer` live-pass finding, 2026-07-29).
   */
  onStateChanged?: (question: Question, nowActive: boolean) => void;
  /**
   * The trigger, handed to the table so it can put focus on the `⋯` of the row that takes the place of
   * one that has just left the list. A callback ref rather than an object one because the table keeps
   * them by question id and has to prune the entry when a row goes.
   */
  triggerRef?: (node: HTMLButtonElement | null) => void;
}

/**
 * §6.9's row menu on a **set's** composition — *Edit* and **one** of *Remove* / *Restore*.
 *
 * ⚠️ **It was the library's row menu until 2026-09-24 and its gate has changed shape, not only
 * subject** (ADR 0019). The menu used to be rendered for a viewer who passed `mayWriteQuestion` — the
 * **question's** author, or an `Admin`, asked per row on `QuestionOut.author_id`. There is no such
 * field and no such rule: **there is one write gate and it is the set's** (ADR 0016 decision 5(c) —
 * composing is writing the set, not the questions in it). So this component asks nothing; the screen
 * asks once, on the set, and either every row carries a `⋯` or no row does. §5 decides the shape:
 * **hidden, never disabled** — a disabled item still tells the viewer the action exists and invites
 * them to ask for it — and it is the **trigger** rather than the items that goes, because a trigger
 * that opens nothing is a control with nothing to do (TASK-072's argument, applied here since
 * TASK-073). **The client gate is never the evidence**; the server refuses a non-author's write.
 *
 * ⚠️ **Editing here edits *this set's* question and nothing else.** Two sets holding the same wording
 * hold two questions with two ids (ADR 0019), so a `PATCH` through this menu cannot reach a colleague's
 * set — which is why nothing on this screen may say a wording is shared, reused or *"in N sets"*.
 *
 * ⚠️ **There is no *Delete* here and there cannot be one** (ADR 0005, **feedback protection #2**) — §7
 * predicts exactly that edit against this menu. Deleting a question would take the recorded feedback
 * that quotes it: `Score.question` is `PROTECT`, the API declares no `DELETE` operation on any path of
 * this surface, and *Remove* is the answer — the row, its wording, its place and every `Score` stay.
 *
 * ⚠️ **Remove and Restore are one item, never both** — `VacancyMenu`'s *Close* / *Reopen* shape, and
 * the same argument: offering *Remove* on an already-archived row is a control with nothing to do, and
 * offering both asks the reader to work out which of them is the current state (the state column
 * already says which, in words). Neither is `destructive`: `DropdownMenu`'s `negative` ink belongs to
 * what cannot be taken back, and a removal is undone from this very menu.
 *
 * ⚠️ **And there is no second control for *Archive*.** With one set per question, removing from the
 * set **is** the archive — one act, one route, one item — and two controls doing one thing is a screen
 * lying about itself (ADR 0019 §5, superseding ADR 0018 decision 5). The word *Archive* survives on
 * this screen only where it names a **state**: the row reads *Archived*, and *Show archived* brings it
 * back into the list.
 *
 * ⚠️ **The item opens a dialog and writes nothing on selection.** A menu item that fired the request
 * itself would have nowhere to report a 403, a throttle or a lost connection — TASK-072's reason for
 * confirming even a *Reopen*.
 */
export function QuestionMenu({ setId, question, onStateChanged, triggerRef }: Props) {
  const { t } = useTranslation('questions');
  const [editing, setEditing] = useState(false);
  const [switchingState, setSwitchingState] = useState(false);

  const items: MenuItem[] = [
    { label: t('menu.edit'), onSelect: () => setEditing(true) },
    {
      // Which one it is comes from the row itself — see this module's header for why it is one item and
      // why neither is `destructive`.
      label: question.is_active ? t('menu.remove') : t('menu.restore'),
      onSelect: () => setSwitchingState(true),
    },
  ];

  return (
    <>
      <DropdownMenu
        trigger={
          <Button
            ref={triggerRef}
            variant="ghost"
            size="sm"
            /**
             * §4 Icons: an icon is never the only label. The name carries the **question's own
             * wording**, because a set has one of these per row and *"Actions"* alone would be a
             * screenful of identical controls to a screen reader (§9) — the same reason `VacancyMenu`'s
             * name carries the vacancy's title.
             */
            aria-label={t('menu.label', { text: question.text })}
          >
            <MoreHorizontal size={16} strokeWidth={1.5} aria-hidden />
          </Button>
        }
        items={items}
      />

      {/* Mounted only while open, so the form's defaults — and the confirmation's direction — are the
          question as it is now. */}
      {editing && (
        <QuestionDialog setId={setId} question={question} onClose={() => setEditing(false)} />
      )}
      {switchingState && (
        <RemoveQuestionDialog
          setId={setId}
          question={question}
          onCompleted={onStateChanged}
          onClose={() => setSwitchingState(false)}
        />
      )}
    </>
  );
}
